// src/components/app_components/analytics/DonutChart.js
import React from "react";
import {
  Pie,
  PieChart as ReChartsDonutChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  Cell
} from "recharts";
import clsx from "clsx";

const sumNumericArray = (arr) => arr.reduce((sum, num) => sum + num, 0);

const parseData = (data, categoryColors, category) =>
    data.map((dataPoint) => ({
      ...dataPoint,
      color: categoryColors.get(dataPoint[category]) || "#3B82F6",
    }));

const calculateDefaultLabel = (data, valueKey) =>
  sumNumericArray(data.map((dataPoint) => dataPoint[valueKey]));

const parseLabelInput = (labelInput, valueFormatter, data, valueKey) =>
  labelInput || valueFormatter(calculateDefaultLabel(data, valueKey));

const ChartTooltip = ({ active, payload, valueFormatter }) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-md border border-gray-200 bg-white p-4 text-sm shadow-md">
          <div className="space-y-1">
            {payload.map(({ value, name, color }, index) => (
              <div
                key={`id-${index}`}
                className="flex items-center justify-between space-x-8"
              >
                <div className="flex items-center space-x-2">
                  <span 
                    aria-hidden="true" 
                    className="h-2 w-2 rounded-full" 
                    style={{ backgroundColor: color }}
                  />
                  <p className="text-gray-700">{name}</p>
                </div>
                <p className="font-medium text-gray-900">
                  {valueFormatter(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
};

const renderInactiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      opacity={0.3}
      style={{ outline: "none" }}
    />
  );
};

const Legend = ({ categories, categoryColors, valueFormatter, data, valueKey }) => {
  // Calculate total for each category
  const categoryTotals = {};
  data.forEach(item => {
    const category = item.name;
    const value = item.value;
    if (!categoryTotals[category]) {
      categoryTotals[category] = 0;
    }
    categoryTotals[category] += value;
  });

  // Calculate total of all values
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="mt-4 space-y-2">
      {categories.map((category) => {
        const value = categoryTotals[category] || 0;
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        
        return (
          <div key={category} className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="h-3 w-3 rounded-full mr-2" 
                style={{ backgroundColor: categoryColors.get(category) }}
              />
              <span className="text-sm text-gray-600 truncate max-w-[100px]">
                {category}
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                {valueFormatter(value)}
              </div>
              <div className="text-xs text-gray-500">
                {percentage}%
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DonutChart = ({
  data = [],
  value = "value",
  category = "name",
  colors = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"],
  variant = "donut",
  valueFormatter = (value) => value.toString(),
  label,
  showLabel = false,
  showTooltip = true,
  showLegend = true, // New prop to control legend visibility
  onValueChange,
  tooltipCallback,
  customTooltip,
  className,
  ...other
}) => {
  const CustomTooltip = customTooltip;
  const [activeIndex, setActiveIndex] = React.useState(undefined);
  const isDonut = variant === "donut";
  const parsedLabelInput = parseLabelInput(label, valueFormatter, data, value);

  const categories = Array.from(new Set(data.map((item) => item[category])));
  const categoryColors = new Map(
    categories.map((cat, index) => [cat, colors[index % colors.length]])
  );

  const prevActiveRef = React.useRef(undefined);
  const prevCategoryRef = React.useRef(undefined);

  const handleShapeClick = (data, index, event) => {
    event.stopPropagation();
    if (!onValueChange) return;

    if (activeIndex === index) {
      setActiveIndex(undefined);
      onValueChange(null);
    } else {
      setActiveIndex(index);
      onValueChange({
        eventType: "sector",
        categoryClicked: data.payload[category],
        ...data.payload,
      });
    }
  };

  return (
    <div className={clsx("w-full", className)} {...other}>
      <div className="flex">
        {/* Chart container - takes 2/3 width */}
        <div className="w-2/3 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <ReChartsDonutChart
              onClick={
                onValueChange && activeIndex !== undefined
                  ? () => {
                      setActiveIndex(undefined);
                      onValueChange(null);
                    }
                  : undefined
              }
              margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
            >
              {showLabel && isDonut && (
                <text
                  className="fill-gray-700 text-sm font-medium"
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {parsedLabelInput}
                </text>
              )}
              <Pie
                className={clsx(
                  "stroke-white [&_.recharts-pie-sector]:outline-none",
                  onValueChange ? "cursor-pointer" : "cursor-default"
                )}
                data={parseData(data, categoryColors, category)}
                cx="50%"
                cy="50%"
                startAngle={90}
                endAngle={-270}
                innerRadius={isDonut ? "60%" : "0%"}
                outerRadius="90%"
                stroke=""
                strokeLinejoin="round"
                dataKey={value}
                nameKey={category}
                isAnimationActive={true}
                onClick={handleShapeClick}
                activeIndex={activeIndex}
                activeShape={(props) => (
                  <Sector
                    {...props}
                    outerRadius={props.outerRadius + 5}
                    fill={props.fill}
                  />
                )}
                inactiveShape={renderInactiveShape}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={categoryColors.get(entry[category])} 
                    stroke="#fff"
                  />
                ))}
              </Pie>
              {showTooltip && (
                <Tooltip
                  wrapperStyle={{ outline: "none" }}
                  content={({ active, payload }) => {
                    const cleanPayload = payload
                      ? payload.map((item) => ({
                          name: item.payload[category],
                          value: item.value,
                          color: categoryColors.get(item.payload[category]),
                        }))
                      : [];

                    const payloadCategory = cleanPayload[0]?.name;

                    if (
                      tooltipCallback &&
                      (active !== prevActiveRef.current ||
                        payloadCategory !== prevCategoryRef.current)
                    ) {
                      tooltipCallback({
                        active,
                        payload: cleanPayload,
                      });
                      prevActiveRef.current = active;
                      prevCategoryRef.current = payloadCategory;
                    }

                    return showTooltip && active ? (
                      CustomTooltip ? (
                        <CustomTooltip active={active} payload={cleanPayload} />
                      ) : (
                        <ChartTooltip
                          active={active}
                          payload={cleanPayload}
                          valueFormatter={valueFormatter}
                        />
                      )
                    ) : null;
                  }}
                />
              )}
            </ReChartsDonutChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend container - takes 1/3 width */}
        {showLegend && (
          <div className="w-1/3 pl-4">
            <div className="h-48 overflow-y-auto pr-2">
              <Legend 
                categories={categories}
                categoryColors={categoryColors}
                valueFormatter={valueFormatter}
                data={data}
                valueKey={value}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonutChart;